import React, { useState, useEffect } from 'react';
import { apiCallJson } from '../utils/auth';
import styles from './ClientMemoModal.module.css';

const TEXT = {
  title: { ko: '메모 작성', en: 'Write Memo', fr: 'Écrire un mémo' },
  placeholder: { ko: '메모를 입력하세요...', en: 'Enter your memo...', fr: 'Entrez votre mémo...' },
  write: { ko: '작성', en: 'Write', fr: 'Écrire' },
  cancel: { ko: '취소', en: 'Cancel', fr: 'Annuler' },
  loading: { ko: '작성 중...', en: 'Writing...', fr: 'Écriture...' },
  maxLength: { ko: '최대 1000자까지 입력 가능합니다.', en: 'Maximum 1000 characters allowed.', fr: 'Maximum 1000 caractères autorisés.' },
  contentRequired: { ko: '메모 내용을 입력해주세요.', en: 'Please enter memo content.', fr: 'Veuillez entrer le contenu du mémo.' }
};

const t = (key, lang) => TEXT[key]?.[lang] || TEXT[key]?.['ko'] || key;

export default function ClientMemoModal({ isOpen, onClose, onMemoAdded, userInfo, currentTheme }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  const lang = userInfo?.preferred_language || 'ko';
  const maxLength = 1000;

  useEffect(() => {
    if (isOpen) {
      setContent('');
      setCharCount(0);

      // body 스크롤 방지
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    if (newContent.length <= maxLength) {
      setContent(newContent);
      setCharCount(newContent.length);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      alert(t('contentRequired', lang));
      return;
    }

    setLoading(true);
    try {
      const response = await apiCallJson('/api/client-memos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: content.trim() })
      });

      if (response) {
        setContent('');
        setCharCount(0);
        onClose();
        if (onMemoAdded) onMemoAdded();
      }
    } catch (error) {
      console.error('메모 작성 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} style={{
        backgroundColor: currentTheme?.backgroundColor === '#151515' ? '#000080' : '#ffffff',
        border: currentTheme?.backgroundColor === '#151515' ? '1px solid #00ff00' : '1px solid #e0e0e0',
        color: currentTheme?.inactiveColor
      }}>
        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className={styles.closeButton}
        >
          ×
        </button>

        {/* 메모 입력 영역 */}
        <div className={styles.inputContainer} style={{ position: 'relative' }}>
          <textarea
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder=""
            className={styles.textarea}
            style={{
              border: isFocused
                ? `1px dashed ${currentTheme?.backgroundColor === '#151515' ? '#ffff00' : '#c00d0d'}`
                : `1px dashed ${currentTheme?.activeColor}`,
              backgroundColor: 'transparent',
              color: currentTheme?.backgroundColor === '#151515' ? '#ffffff' : '#333333',
              padding: '0 8px',
              lineHeight: '32px',
              boxSizing: 'border-box',
              height: '32px',
              overflow: 'hidden',
              caretColor: currentTheme?.activeColor
            }}
          />
          {!isFocused && <span className={styles.cursorAnimation}>|</span>}

          {/* 보내기 버튼 */}
          <button
            onClick={handleSubmit}
            disabled={loading || !content.trim()}
            className={styles.submitButton}
          >
            {loading ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="m9 12 2 2 4-4"></path>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                <path d="M2 2l7.586 7.586"></path>
                <circle cx="11" cy="11" r="2"></circle>
              </svg>
            )}
          </button>
        </div>

        {/* 글자 수 표시 */}
        <div
          className={`${styles.charCount} ${charCount > maxLength * 0.9 ? styles.warning : ''}`}
          style={{
            color: currentTheme?.backgroundColor === '#151515' ? '#00ff00' : '#c00d0d'
          }}
        >
          {charCount}/{maxLength}
        </div>
      </div>
    </div>
  );
}
